/**
 * Storefront request timeout -- classification and the error contract.
 *
 * A shopper page must never hang blank on a request that never answers
 * (docs/planning/2026-10-02-storefront-session-everywhere-plan.md, "Request
 * timeout"). So storefront requests carry a timeout, applied by the request
 * interceptor in `@/axios`. Which requests:
 *
 *   - Only while the address bar is on a storefront page (`/shop/<slug>/...`
 *     or the legacy `/store/<slug>/...`, per `currentStorefrontSlug`). Admin
 *     and back-office pages are unchanged.
 *   - GET / HEAD: reads. Aborting one costs nothing but a retry.
 *   - The session-establishing POSTs (`/auth/getToken/<id>`,
 *     `/auth/getStoreToken/<id>`, `/auth/resumeStorefrontSession`): the gate
 *     cannot render the store without them, and a duplicate mint on retry is
 *     harmless (the newest session wins).
 *   - NOT any other write. Checkout, create_pending_order and the cart writes
 *     may still COMPLETE server-side after a client abort; a shopper who then
 *     retries can place a duplicate order. Those are left untimed on purpose.
 *   - A caller's own explicit `timeout` is respected.
 *
 * ERROR CONTRACT (for StorefrontSessionGate and any storefront caller): a
 * timed-out storefront request rejects with an AxiosError whose
 * `code === 'ECONNABORTED'` (`'ETIMEDOUT'` if axios's clarifyTimeoutError is
 * ever enabled), `response` undefined, and `config.storefrontTimed === true`.
 * `isStorefrontRequestTimeout(err)` is the one predicate for it. It is a
 * RETRYABLE failure: show "trouble opening the store, try again", never
 * "Store not found".
 *
 * THIS MODULE MUST STAY A LEAF apart from storefrontSlug.js (itself a leaf):
 * `@/axios` imports it while still evaluating (see sessionProvenance.js and
 * __tests__/axios-import-cycle.spec.js).
 */
import { currentStorefrontSlug } from "./storefrontSlug";

export const STOREFRONT_REQUEST_TIMEOUT_MS = 20_000;

/** Request-config key set on every request this module put a timeout on. */
export const STOREFRONT_TIMED = "storefrontTimed";

const SESSION_ESTABLISHING_PATH =
    /(^|\/)auth\/(getToken\/[^/?#]+|getStoreToken\/[^/?#]+|resumeStorefrontSession)\/?($|\?)/;

const TIMEOUT_CODES = new Set(["ECONNABORTED", "ETIMEDOUT"]);

function isSessionEstablishingPost(method, url) {
    return method === "post" && SESSION_ESTABLISHING_PATH.test(url);
}

/** Should this request carry the storefront timeout? */
export function shouldTimeStorefrontRequest(config) {
    if (!config || currentStorefrontSlug() === null) return false;
    const method = String(config.method || "get").toLowerCase();
    if (method === "get" || method === "head") return true;
    const url = typeof config.url === "string" ? config.url : "";
    return isSessionEstablishingPost(method, url);
}

/** Request-interceptor step: apply the timeout when it is in scope. */
export function applyStorefrontRequestTimeout(config) {
    if (config && !config.timeout && shouldTimeStorefrontRequest(config)) {
        config.timeout = STOREFRONT_REQUEST_TIMEOUT_MS;
        config[STOREFRONT_TIMED] = true;
    }
    return config;
}

/** True when `err` is a storefront request that hit this module's timeout. */
export function isStorefrontRequestTimeout(err) {
    return Boolean(
        err &&
            TIMEOUT_CODES.has(err.code) &&
            err.config &&
            err.config[STOREFRONT_TIMED] === true,
    );
}
